import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  ProductInput,
  createProduct,
  deleteProduct,
  getProducts,
  updateProduct,
} from '../lib/api';
import { Product } from '../types';
import { ProductFormModal } from '../components/ProductFormModal';
import { useConfirm, useToast } from '../ui/UIContext';
import { AdminTableSkeleton } from '../components/AdminSkeletons';

export function AdminProductsPage() {
  const { t } = useTranslation();
  const showToast = useToast();
  const confirm = useConfirm();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Product | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        setProducts(await getProducts());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleCreate(input: ProductInput) {
    const created = await createProduct(input);
    setProducts((prev) => [created, ...prev]);
    setCreating(false);
    showToast(t('adminProducts.toast.created'), 'success');
  }

  async function handleUpdate(input: ProductInput) {
    if (!editing) return;
    const updated = await updateProduct(editing.id, input);
    setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setEditing(null);
    showToast(t('adminProducts.toast.updated'), 'success');
  }

  async function handleDelete(product: Product) {
    const ok = await confirm({
      title: t('adminProducts.confirmDelete.title'),
      message: t('adminProducts.confirmDelete.message', { title: product.title }),
      destructive: true,
      confirmLabel: t('adminProducts.actions.delete'),
    });
    if (!ok) return;
    try {
      await deleteProduct(product.id);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      showToast(t('adminProducts.toast.deleted'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete', 'error');
    }
  }

  if (loading) {
    return <AdminTableSkeleton rows={6} columns={5} />;
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ink">{t('adminProducts.title')}</h1>
          <p className="text-ink-soft mt-1">{t('adminProducts.subtitle')}</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" />
          {t('adminProducts.new')}
        </button>
      </div>

      {products.length === 0 ? (
        <div className="bg-white rounded-lg p-12 text-center text-gray-500 border border-gray-200">
          {t('adminProducts.empty')}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('adminProducts.table.product')}
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('adminProducts.table.price')}
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('adminProducts.table.duration')}
                  </th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('adminProducts.table.rating')}
                  </th>
                  <th className="px-4 py-3 text-end text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('adminProducts.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.imageUrl}
                          alt=""
                          className="w-12 h-12 rounded object-cover bg-gray-100"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{product.title}</div>
                          <div className="text-xs text-gray-500 max-w-xs truncate">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>${product.price.toFixed(2)}</div>
                      {product.originalPrice && (
                        <div className="text-xs text-gray-400 line-through">
                          ${product.originalPrice.toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {product.measurementDuration}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{product.rating.toFixed(1)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setEditing(product)}
                          title={t('adminProducts.actions.edit')}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          title={t('adminProducts.actions.delete')}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <ProductFormModal
          product={null}
          onClose={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <ProductFormModal
          product={editing}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}
    </section>
  );
}
